/**
 * client/src/pages/Read.tsx — the route that hands the screen over to the reader.
 *
 * This file is deliberately thin. Everything about pagination, typography, the highlight and the page
 * turn belongs to `client/src/reader/Reader.tsx`; what belongs *here* is the one question that must
 * be answered before that component is worth mounting: **is there anything to read yet?** A reader
 * mounted over an edition that is still importing measures an empty track, gets a page count of zero,
 * and looks broken — so a book that is not `ready` is sent back to its own screen, which is where the
 * import reports itself.
 *
 * ⚠️ **No `<AppFrame>`, and that is structural, not stylistic.** §5.1's frame is a fixed-size,
 * non-scrolling box whose height is written in *pixels* by JS, and §5.2's whole argument is that a
 * non-scrolling document is what keeps iOS Safari's URL bar from resizing the viewport mid-session.
 * A sticky app header above the reader would eat the measured `--frame-h` and put a scroll container
 * back into the page.
 *
 * ⚠️ **Not behind `<Gate>` either** (see routes.tsx): this is the screen a guest actually gets, and
 * `readAccess` on the server is what decides. `GET /s/:token` redirects a phone that has never seen
 * the site straight here, so a client-side sign-in wall would break the flagship flow outright.
 *
 * ⚠️ One rough edge, recorded rather than hidden: a share link to a book whose ingest has **not
 * landed** sends a guest here, this route bounces them to `/b/:id` for the honest import screen, and
 * that route IS gated — so they arrive at a sign-in wall. The owner's share screen is reachable
 * before the edition is ready, so the state is producible. See docs/P4-STATUS.md.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Blank } from "../App.tsx";
import { ApiError, abApi } from "../lib/api.ts";
import type { BookDetail } from "../lib/api.ts";
import { loadContinuousMode, pickNextBook } from "../lib/continuous.ts";
import { useCachedQuery } from "../lib/idb.ts";
import { Reader } from "../reader/Reader.tsx";
import { useNarration } from "../reader/useNarration.ts";
import type { NarrationBook } from "../reader/useNarration.ts";

/**
 * The query flag that carries "keep playing" across a book boundary.
 *
 * ⚠️ **This survives an autoplay policy that would refuse a fresh tab, and only because the route
 * change is a client-side navigation.** `AudioEngine` is a module-level singleton that lives for the
 * life of the *document*, and both its `<audio>` elements were unlocked inside a real gesture when
 * the listener pressed play on the previous book. Navigating within the SPA keeps that document — and
 * therefore that unlock — so playback resumes with no gesture. A hard reload of this URL is a new
 * document with no unlock, and the engine reports `needs-gesture` rather than failing silently, which
 * is the correct answer to someone who pasted the link into a cold tab.
 */
const AUTOPLAY_PARAM = "autoplay";

export function Read() {
  const { bookId = "" } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const detail = useCachedQuery<BookDetail>(`book:${bookId}`, () => abApi.book(bookId));
  const title = detail.data?.book.title ?? null;
  const edition = detail.data?.edition ?? null;

  /**
   * ⚠️ **Memoised, and that is load-bearing rather than tidy.**
   *
   * `useNarration` keys its page-index fetch and its Media Session binding on this object's fields.
   * A fresh object every render would refetch the page list and rebind the lock screen on every
   * highlight change — several times a minute, forever, with the screen off. Stable identity is what
   * makes the narration plug cost nothing while it is only listening.
   */
  const narrationBook = useMemo<NarrationBook | null>(() => {
    const b = detail.data?.book;
    if (!b || !edition || edition.state !== "ready") return null;
    return {
      bookId: b.id,
      editionId: edition.id,
      title: b.title,
      author: b.author ?? null,
      hasCover: Boolean(b.coverSha),
      // Whose voice, for the follow pill. Not the voice *id* — `karti@v1` on screen would be
      // a leaked implementation detail in a sentence a person reads.
      voiceLabel: "Karti",
    };
  }, [detail.data?.book, edition]);

  /**
   * ⚠️ **One continuation per book, enforced by a ref.**
   *
   * `end-of-book` is emitted once by the engine, but the *handler* re-runs a shelf fetch and a
   * navigation, and a stray second event — a resync at the last page, a re-subscribe — would start a
   * second navigation on top of the first. The ref is reset when the mounted book changes, so the
   * next book gets its own single continuation.
   */
  const continuedFrom = useRef<string | null>(null);
  useEffect(() => {
    continuedFrom.current = null;
  }, [bookId]);

  const narrationRef = useRef<ReturnType<typeof useNarration>>(null);

  /**
   * The book ran out. `client/src/lib/continuous.ts` holds the policy and the reasoning; this is the
   * half of it that needs a router and an edition.
   */
  const onEndOfBook = useCallback(() => {
    if (continuedFrom.current === bookId) return;
    const mode = loadContinuousMode();
    if (mode === "stop") return;
    continuedFrom.current = bookId;

    if (mode === "repeat") {
      // `bodyStartSid` and not `c000.b0000.s0`: the top of AGENTS.md is this library reading the
      // Project Gutenberg licence aloud, and a repeat that starts at ordinal zero reintroduces it
      // every single night.
      const from = edition?.bodyStartSid ?? null;
      if (from === null) return;
      narrationRef.current?.onPlayFrom?.(from);
      return;
    }

    void (async () => {
      try {
        // `all` rather than `mine`: a shared or public book the listener can already open is a
        // legitimate next read, and `canList` on the server has already filtered this to what they
        // may actually see.
        const page = await abApi.shelf("all", "title", "");
        const next = pickNextBook(page.books, bookId);
        // No other narrated book on the shelf. Stopping is the honest answer — advancing into a book
        // with no audio would stall for three minutes and then blame the network.
        if (next === null) return;
        navigate(`/b/${next.id}/read?${AUTOPLAY_PARAM}=1`);
      } catch {
        // The shelf is unreachable (offline, a dropped tailnet). Silence at the end of the book is
        // the truthful outcome; a retry loop here would be a background fetch nobody asked for.
      }
    })();
  }, [bookId, edition?.bodyStartSid, navigate]);

  const narration = useNarration(narrationBook, { onEndOfBook });
  narrationRef.current = narration;

  /**
   * Arrived here from the previous book's last page: start reading without waiting to be asked.
   *
   * Keyed on the book, once — `onPlayFrom` is safe to call before the page index has loaded (the hook
   * holds the intent in `intendPlay` and starts the moment the planner says ready), so this does not
   * need to wait for anything beyond the edition existing.
   */
  const autoplayed = useRef<string | null>(null);
  useEffect(() => {
    if (search.get(AUTOPLAY_PARAM) !== "1") return;
    if (narration === null || edition === null || autoplayed.current === bookId) return;
    autoplayed.current = bookId;
    // An edition with no body start has not been through the structure editor. Playing from the
    // reader's own position is the safe answer; `onPlayFrom("")` would be a sid that resolves to
    // nothing, which LAW 2 spends a whole file explaining the cost of.
    const from = edition.bodyStartSid;
    if (from !== null) narration.onPlayFrom?.(from);
    else if (narration.playing !== true) narration.onPlayPause?.();
  }, [search, narration, edition, bookId]);

  // The tab title is the book. It is also what the share sheet falls back to, and what the lock
  // screen shows for the moment between the reader mounting and Media Session taking the metadata.
  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = `${title} — audiobook.karti.ai`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  if (detail.loading && !detail.data) return <Blank />;

  if (!detail.data) {
    const missing = detail.error instanceof ApiError && detail.error.notFound;
    return (
      <div className="ab-app">
        <main className="ab-main">
          <div className="ab-empty">
            <h2>{missing ? "No book here." : "Couldn't open that book."}</h2>
            <p>{missing ? "It was deleted, or it isn't yours." : (detail.error?.message ?? "")}</p>
            <Link to="/" className="ab-btn ab-btn-primary">
              Back to the shelf
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Still importing, failed, or never got an edition: the book screen is the honest place for all
  // three, and it is already listening to the job stream.
  if (detail.data.edition?.state !== "ready") {
    return <Navigate to={`/b/${bookId}`} replace />;
  }

  return (
    <div className="ab-reader-route">
      {/* The way out. `navigate` rather than a `<Link>` because the reader draws the control itself
          inside its own bar — the frame has no room for chrome that is not the book. */}
      <Reader bookId={bookId} onExit={() => navigate(`/b/${bookId}`)} narration={narration} />
    </div>
  );
}
